import React, { FormEventHandler, useCallback, useEffect, useRef } from 'react'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'LuxKit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import LegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import SocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import { useMutation } from '@tanstack/react-query'
import { checkUserExists } from 'api/auth'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import { CHECK_USER_EXISTS } from 'actions/apiActionConstants'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import noop from 'lib/function/noop'
import AccountAccessTrustIndicatorsMapper from 'components/Account/AccountAccess/AccountAccessTrustIndicatorsMapper'
import requestCheckEmailAssociation from 'businessTraveller/api/requestCheckEmailAssociation'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE } from 'components/App/Routes/routeConstants'
import { AccountAccessSurface } from 'components/Account/AccountAccess/AccountAccessModalWrapper'

interface LoginFormData {
  email: string
}

interface MappedStateProps {
  authError?: App.ApiCallFailure
}

interface Props {
  onModeChange?: (mode: App.UiAccountModalMode) => void;
  onSubmit?: (userId: string) => void;
  variant?: AccountAccessSurface;
}

const COPY: Record<AccountAccessSurface, { heading: string; subtitle: string; }> = {
  modal: {
    heading: 'Travelling for work has never been easier',
    subtitle: 'Manage your business bookings and expenses – all in one place.',
  },
  page: {
    heading: 'Sign in to book a work trip',
    subtitle: 'Your place to book and manage work travel',
  },
}

function BusinessTravellerLoginForm(props: Props & MappedStateProps) {
  const {
    authError,
    onModeChange = noop,
    onSubmit = noop,
    variant = 'modal',
  } = props

  const isPage = variant === 'page'
  const copy = COPY[variant]
  const isDesktopUp = useScreenSizeUp('desktop')

  const appDispatch = useAppDispatch()
  const modalContext = useModalElementContext()

  const formRef = useRef<HTMLFormElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const emailCheckMutation = useMutation <true, string, LoginFormData>({
    mutationFn: async(variables) => {
      const recaptchaResponse = await handleReCAPTCHA(recaptchaRef.current)
      try {
        const emailCheckResponse = await checkUserExists({ email: variables.email }, recaptchaResponse)
        if (!emailCheckResponse.isExist) {
          // in the context of business traveller login
          // a non-existing email is considered an error state
          // hence the rejected promise
          return Promise.reject('This email is not registered.')
        }

        // this function can only throw an error or return true
        await requestCheckEmailAssociation(variables.email)

        return true
      } catch(error) {
        return Promise.reject((error as App.ApiCallFailure).message || 'There was a problem checking this email!')
      }
    },
    onSuccess: (data, variables) => {
      // assuming the req promise has been rejected if the email doesn't exist
      appDispatch({
        type: API_CALL_SUCCESS,
        api: CHECK_USER_EXISTS,
        data: { isExist: data },
        email: variables.email,
        key: variables.email,
      })
      onSubmit(variables.email)
      onModeChange('loginPassword')
    },
  })

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const formData = formToObject<LoginFormData>(event.currentTarget)
    emailCheckMutation.mutate(formData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSocialMediaLoginEnabled = !!(config.GOOGLE_APP_ID)

  useEffect(() => {
    emailCheckMutation.reset()
    formRef.current?.reset()
    emailInputRef.current?.focus()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <form
      ref={formRef}
      name="businessTravellerLoginForm"
      data-testid="business-traveller-login-form"
      onSubmit={handleSubmission}
      style={isPage ? { paddingTop: isDesktopUp ? 48 : 28 } : undefined}
    >
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={24}>
          {authError?.message && <MessageBanner
            kind="critical"
            description={authError.message}
          />}
          {emailCheckMutation.error && <MessageBanner
            kind="critical"
            description={emailCheckMutation.error}
          />}

          {!isPage && <Group direction="horizontal" horizontalAlign="center"><LogoIcon /></Group>}

          <Group direction="vertical" gap={4}>
            <Heading variant={isPage ? 'heading2' : 'heading4'} align={isPage ? 'start' : 'center'}>
              {copy.heading}
            </Heading>
            <BodyText variant="medium" align={isPage ? 'start' : 'center'}>
              {copy.subtitle}
            </BodyText>
          </Group>

          {!isPage && config.LOGIN_TRUST_INDICATORS && <AccountAccessTrustIndicatorsMapper />}

          <Group direction="vertical" gap={12}>
            <TextInput
              label="Email address"
              required
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Email"
              requiredErrorMessage="Email is required"
              ref={emailInputRef}
            />
            <Group direction="horizontal" gap={8}>
              <Group direction="horizontal" fullWidth>
                <TextButton
                  kind="primary"
                  size="large"
                  data-testid="to-login-continue"
                  type="submit"
                  fit="flex"
                >
                  Continue
                </TextButton>
              </Group>
              {!isPage && <Group direction="horizontal" fullWidth>
                <TextButton
                  kind="secondary"
                  size="large"
                  fit="flex"
                  to={BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE}
                  onClick={() => modalContext.resolve()}
                >
                  Sign up
                </TextButton>
              </Group>}
            </Group>
          </Group>

          {isSocialMediaLoginEnabled && <>
            <Group direction="horizontal" gap={12} verticalAlign="center">
              <Divider kind="primary" />
              <BodyText variant="large">or</BodyText>
              <Divider kind="primary" />
            </Group>

            <SocialButtons mode="login" />
          </>}
        </Group>
        <LegalText />
      </Group>
      {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />}
    </form>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    authError: state.auth.error,
  }
})(BusinessTravellerLoginForm)
